// src/components/UserDetailsModal.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import Select from "react-select";

import {
  FiCalendar,
  FiCreditCard,
  FiEdit3,
  FiGlobe,
  FiHome,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSave,
  FiShield,
  FiUser,
  FiX,
} from "react-icons/fi";

import axios from "../axiosConfig";

import "../styles/UserDetailsModal.css";

const ROLE_KEYS = [
  "admin",
  "manager",
  "dispatcher",
  "accountant"
];

const MAX_FILE_SIZE =
  5 * 1024 * 1024;

const deepEqual = (
  a,
  b
) =>
  JSON.stringify(
    a
  ) ===
  JSON.stringify(
    b
  );

const toInputDate = (
  value
) => {
  if (!value) {
    return "";
  }

  if (
    typeof value ===
      "string" &&
    /^\d{4}-\d{2}-\d{2}/.test(
      value
    )
  ) {
    return value.slice(
      0,
      10
    );
  }

  const date =
    new Date(
      value
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const year =
    date.getUTCFullYear();

  const month =
    String(
      date.getUTCMonth() +
        1
    ).padStart(
      2,
      "0"
    );

  const day =
    String(
      date.getUTCDate()
    ).padStart(
      2,
      "0"
    );

  return `${year}-${month}-${day}`;
};

const fromInputDate = (
  value
) =>
  value ||
  null;

const userDetailsSelectStyles = {
  control: (
    base,
    state
  ) => ({
    ...base,

    minHeight:
      42,

    height:
      42,

    borderRadius:
      10,

    paddingLeft:
      2,

    background:
      "var(--udm-field-bg)",

    borderColor:
      state.isFocused
        ? "var(--udm-primary)"
        : "var(--udm-border)",

    boxShadow:
      state.isFocused
        ? "0 0 0 3px var(--udm-focus-halo)"
        : "none",

    "&:hover": {
      borderColor:
        state.isFocused
          ? "var(--udm-primary)"
          : "var(--udm-border-strong)",
    },

    cursor:
      "pointer",
  }),

  valueContainer: (
    base
  ) => ({
    ...base,

    height:
      42,

    padding:
      "0 11px",
  }),

  placeholder: (
    base
  ) => ({
    ...base,

    color:
      "var(--udm-placeholder)",

    fontSize:
      13,
  }),

  singleValue: (
    base
  ) => ({
    ...base,

    color:
      "var(--udm-text)",

    fontSize:
      13,
  }),

  input: (
    base
  ) => ({
    ...base,

    color:
      "var(--udm-text)",

    fontSize:
      13,
  }),

  indicatorsContainer: (
    base
  ) => ({
    ...base,

    height:
      42,
  }),

  indicatorSeparator:
    () => ({
      display:
        "none",
    }),

  clearIndicator: (
    base
  ) => ({
    ...base,

    color:
      "var(--udm-muted)",

    padding:
      6,

    ":hover": {
      color:
        "var(--udm-text)",
    },
  }),

  dropdownIndicator: (
    base,
    state
  ) => ({
    ...base,

    color:
      state.isFocused
        ? "var(--udm-text)"
        : "var(--udm-muted)",

    padding:
      "0 10px 0 6px",

    ":hover": {
      color:
        "var(--udm-text)",
    },
  }),

  menu: (
    base
  ) => ({
    ...base,

    zIndex:
      1700,

    marginTop:
      7,

    overflow:
      "hidden",

    border:
      "1px solid var(--udm-border)",

    borderRadius:
      11,

    background:
      "var(--udm-panel-solid)",

    boxShadow:
      "0 18px 42px rgba(0, 0, 0, 0.34)",
  }),

  menuList: (
    base
  ) => ({
    ...base,

    padding:
      6,
  }),

  option: (
    base,
    state
  ) => ({
    ...base,

    borderRadius:
      8,

    color:
      "var(--udm-text)",

    backgroundColor:
      state.isSelected
        ? "var(--udm-primary-soft)"
        : state.isFocused
          ? "var(--udm-field-hover)"
          : "transparent",

    fontSize:
      13,

    cursor:
      "pointer",

    ":active": {
      backgroundColor:
        "var(--udm-primary-soft-strong)",
    },
  }),
};

function FieldLabel({
  children,
  required = false,
  icon: Icon,
}) {
  return (
    <label className="udm-field-label">
      {Icon && (
        <Icon
          className="udm-field-label-icon"
          aria-hidden="true"
        />
      )}

      <span className="udm-field-label-text">
        {children}

        {required && (
          <span
            className="udm-required"
            aria-hidden="true"
          >
            *
          </span>
        )}
      </span>
    </label>
  );
}

export default function UserDetailsModal({
  userId,
  onClose,
  onSaved,
}) {
  const { t } =
    useTranslation(
      "user_details_modal"
    );

  const [
    loading,
    setLoading,
  ] = useState(
    true
  );

  const [
    saving,
    setSaving,
  ] = useState(
    false
  );

  const [
    error,
    setError,
  ] = useState(
    ""
  );

  const [
    base,
    setBase,
  ] = useState(
    null
  );

  const [
    form,
    setForm,
  ] = useState(
    null
  );

  const [
    file,
    setFile,
  ] = useState(
    null
  );

  const [
    profileSourcePreview,
    setProfileSourcePreview,
  ] = useState(
    null
  );

  const [
    adjustedProfileFile,
    setAdjustedProfileFile,
  ] = useState(
    null
  );

  const [
    adjustedProfilePreview,
    setAdjustedProfilePreview,
  ] = useState(
    null
  );

  const [
    profileEditorOpen,
    setProfileEditorOpen,
  ] = useState(
    false
  );

  const [
    profileZoom,
    setProfileZoom,
  ] = useState(
    1
  );

  const [
    profilePositionX,
    setProfilePositionX,
  ] = useState(
    50
  );

  const [
    profilePositionY,
    setProfilePositionY,
  ] = useState(
    50
  );

  const fileInputRef =
    useRef(
      null
    );

  const idExpRef =
    useRef(
      null
    );

  const dobRef =
    useRef(
      null
    );

  const closeModal =
    useCallback(
      () => {
        onClose?.();
      },
      [
        onClose,
      ]
    );

  const openPicker = (
    ref
  ) => {
    if (
      ref?.current
        ?.showPicker
    ) {
      ref.current.showPicker();

      return;
    }

    ref?.current
      ?.focus();
  };

  const handleFilePick =
    () => {
      fileInputRef
        .current
        ?.click();
    };

  const onChange = (
    key,
    value
  ) => {
    setForm(
      (
        previous
      ) => ({
        ...previous,

        [key]:
          value,
      })
    );

    setError(
      ""
    );
  };

  const roleOptions =
    useMemo(
      () =>
        ROLE_KEYS.map(
          (
            key
          ) => ({
            value:
              key,

            label:
              t(
                `roles.${key}`
              ),
          })
        ),
      [
        t,
      ]
    );

  const roleValue =
    useMemo(
      () =>
        roleOptions.find(
          (
            option
          ) =>
            option.value ===
            form?.role
        ) ||
        null,
      [
        form?.role,
        roleOptions,
      ]
    );

  useEffect(
    () => {
      let cancelled =
        false;

      const controller =
        new AbortController();

      const load =
        async () => {
          setLoading(
            true
          );

          setError(
            ""
          );

          try {
            const {
              data,
            } =
              await axios.get(
                `/users/${userId}`,
                {
                  signal:
                    controller.signal,
                }
              );

            const payload =
              data?.data ??
              data;

            if (
              cancelled
            ) {
              return;
            }

            const normalized = {
              ...payload,

              email:
                payload?.email ??
                payload?.auth
                  ?.email ??
                "",

              idExpiryDate:
                toInputDate(
                  payload
                    ?.idExpiryDate
                ),

              dateOfBirth:
                toInputDate(
                  payload
                    ?.dateOfBirth
                ),
            };

            setBase(
              normalized
            );

            setForm({
              name:
                normalized
                  .name ??
                "",

              email:
                normalized
                  .email ??
                "",

              role:
                normalized
                  .role ??
                "",

              phoneNumber:
                normalized
                  .phoneNumber ??
                "",

              idNumber:
                normalized
                  .idNumber ??
                "",

              idExpiryDate:
                normalized
                  .idExpiryDate ??
                "",

              dateOfBirth:
                normalized
                  .dateOfBirth ??
                "",

              country:
                normalized
                  .country ??
                "",

              city:
                normalized
                  .city ??
                "",

              postalCode:
                normalized
                  .postalCode ??
                "",

              address:
                normalized
                  .address ??
                "",
            });
          } catch (err) {
            if (
              cancelled
            ) {
              return;
            }

            console.error(
              "[users] details fetch error:",
              err
            );

            setError(
              t(
                "errors.load_failed"
              )
            );
          } finally {
            if (
              !cancelled
            ) {
              setLoading(
                false
              );
            }
          }
        };

      load();

      return () => {
        cancelled =
          true;

        controller.abort();
      };
    },
    [
      userId,
      t,
    ]
  );

  useEffect(
    () => {
      if (!file) {
        setProfileSourcePreview(
          null
        );

        return undefined;
      }

      const url =
        URL.createObjectURL(
          file
        );

      setProfileSourcePreview(
        url
      );

      return () => {
        URL.revokeObjectURL(
          url
        );
      };
    },
    [
      file,
    ]
  );

  useEffect(
    () => () => {
      if (
        adjustedProfilePreview
      ) {
        URL.revokeObjectURL(
          adjustedProfilePreview
        );
      }
    },
    [
      adjustedProfilePreview,
    ]
  );

  useEffect(
    () => {
      const previousOverflow =
        document.body.style
          .overflow;

      document.body.style.overflow =
        "hidden";

      return () => {
        document.body.style.overflow =
          previousOverflow;
      };
    },
    []
  );

  useEffect(
    () => {
      const onEsc = (
        event
      ) => {
        if (
          event.key !==
          "Escape"
        ) {
          return;
        }

        if (
          profileEditorOpen
        ) {
          setProfileEditorOpen(
            false
          );

          return;
        }

        closeModal();
      };

      document.addEventListener(
        "keydown",
        onEsc
      );

      return () => {
        document.removeEventListener(
          "keydown",
          onEsc
        );
      };
    },
    [
      closeModal,
      profileEditorOpen,
    ]
  );

  const dirty =
    useMemo(
      () => {
        if (
          !base ||
          !form
        ) {
          return false;
        }

        const comparableBase = {
          name:
            base.name ??
            "",

          email:
            base.email ??
            base.auth
              ?.email ??
            "",

          role:
            base.role ??
            "",

          phoneNumber:
            base.phoneNumber ??
            "",

          idNumber:
            base.idNumber ??
            "",

          idExpiryDate:
            toInputDate(
              base.idExpiryDate
            ) ??
            "",

          dateOfBirth:
            toInputDate(
              base.dateOfBirth
            ) ??
            "",

          country:
            base.country ??
            "",

          city:
            base.city ??
            "",

          postalCode:
            base.postalCode ??
            "",

          address:
            base.address ??
            "",
        };

        return (
          !deepEqual(
            comparableBase,
            form
          ) ||
          !!file
        );
      },
      [
        base,
        form,
        file,
      ]
    );

  const handleProfileFile = (
    event
  ) => {
    const selectedFile =
      event.target
        .files?.[0];

    if (
      !selectedFile
    ) {
      return;
    }

    const isImage =
      /^image\//i.test(
        selectedFile.type
      ) ||
      /\.(heic|heif)$/i.test(
        selectedFile.name
      );

    if (
      !isImage
    ) {
      setError(
        t(
          "errors.invalid_file_profile"
        )
      );

      event.target.value =
        "";

      return;
    }

    if (
      selectedFile.size >
      MAX_FILE_SIZE
    ) {
      setError(
        t(
          "errors.file_too_large"
        )
      );

      event.target.value =
        "";

      return;
    }

    setError(
      ""
    );

    setFile(
      selectedFile
    );

    setAdjustedProfileFile(
      null
    );

    if (
      adjustedProfilePreview
    ) {
      URL.revokeObjectURL(
        adjustedProfilePreview
      );
    }

    setAdjustedProfilePreview(
      null
    );

    setProfileZoom(
      1
    );

    setProfilePositionX(
      50
    );

    setProfilePositionY(
      50
    );

    setProfileEditorOpen(
      true
    );

    event.target.value =
      "";
  };

  const createAdjustedProfileFile =
    async () => {
      if (
        !file ||
        !profileSourcePreview
      ) {
        return null;
      }

      const image =
        await new Promise(
          (
            resolve,
            reject
          ) => {
            const img =
              new Image();

            img.onload =
              () =>
                resolve(
                  img
                );

            img.onerror =
              reject;

            img.src =
              profileSourcePreview;
          }
        );

      const outputSize =
        512;

      const canvas =
        document.createElement(
          "canvas"
        );

      canvas.width =
        outputSize;

      canvas.height =
        outputSize;

      const context =
        canvas.getContext(
          "2d"
        );

      if (
        !context
      ) {
        return null;
      }

      const imageWidth =
        image.naturalWidth;

      const imageHeight =
        image.naturalHeight;

      const baseCropSize =
        Math.min(
          imageWidth,
          imageHeight
        );

      const cropSize =
        baseCropSize /
        profileZoom;

      const maxX =
        Math.max(
          0,
          imageWidth -
            cropSize
        );

      const maxY =
        Math.max(
          0,
          imageHeight -
            cropSize
        );

      const sourceX =
        maxX *
        (
          profilePositionX /
          100
        );

      const sourceY =
        maxY *
        (
          profilePositionY /
          100
        );

      context.drawImage(
        image,
        sourceX,
        sourceY,
        cropSize,
        cropSize,
        0,
        0,
        outputSize,
        outputSize
      );

      const blob =
        await new Promise(
          (
            resolve
          ) => {
            canvas.toBlob(
              resolve,
              "image/jpeg",
              0.92
            );
          }
        );

      if (
        !blob
      ) {
        return null;
      }

      return new File(
        [
          blob,
        ],
        "profile-picture.jpg",
        {
          type:
            "image/jpeg",
        }
      );
    };

  const confirmProfileAdjustment =
    async () => {
      try {
        const adjustedFile =
          await createAdjustedProfileFile();

        if (
          !adjustedFile
        ) {
          throw new Error(
            "Unable to create adjusted profile picture"
          );
        }

        setAdjustedProfileFile(
          adjustedFile
        );

        if (
          adjustedProfilePreview
        ) {
          URL.revokeObjectURL(
            adjustedProfilePreview
          );
        }

        setAdjustedProfilePreview(
          URL.createObjectURL(
            adjustedFile
          )
        );

        setProfileEditorOpen(
          false
        );
      } catch (err) {
        console.error(
          "[users] photo adjustment error:",
          err
        );

        setError(
          t(
            "errors.photo_adjust_failed"
          )
        );
      }
    };

  const handleSave =
    async () => {
      if (
        !form ||
        !dirty
      ) {
        return;
      }

      setSaving(
        true
      );

      setError(
        ""
      );

      try {
        let response;

        const finalProfileFile =
          adjustedProfileFile ||
          file;

        const normalizedForm = {
          ...form,

          idExpiryDate:
            fromInputDate(
              form.idExpiryDate
            ),

          dateOfBirth:
            fromInputDate(
              form.dateOfBirth
            ),
        };

        if (
          finalProfileFile
        ) {
          const fd =
            new FormData();

          Object.entries(
            normalizedForm
          ).forEach(
            ([
              key,
              value,
            ]) => {
              fd.append(
                key,
                value ??
                  ""
              );
            }
          );

          fd.append(
            "profilePicture",
            finalProfileFile
          );

          response =
            await axios.put(
              `/users/${userId}`,
              fd,
              {
                headers: {
                  "Content-Type":
                    "multipart/form-data",
                },
              }
            );
        } else {
          response =
            await axios.put(
              `/users/${userId}`,
              normalizedForm
            );
        }

        const updatedRaw =
          response?.data
            ?.data ??
          response?.data ??
          normalizedForm;

        const updated = {
          ...updatedRaw,

          id:
            updatedRaw?.id ??
            updatedRaw
              ?.user_id ??
            userId,
        };

        onSaved?.(
          updated
        );

        closeModal();
      } catch (err) {
        console.error(
          "[users] update error:",
          err
        );

        const message =
          err?.response
            ?.data
            ?.error ||
          err?.response
            ?.data
            ?.message ||
          t(
            "errors.save_failed"
          );

        setError(
          message
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  const visibleProfilePicture =
    adjustedProfilePreview ||
    profileSourcePreview ||
    base?.profilePictureUrl ||
    null;

  const renderHeader =
    () => (
      <header className="udm-header">
        <div className="udm-header-copy">
          <h2 id="user-details-modal-title">
            {t(
              "title"
            )}
          </h2>

          <p>
            {t(
              "subtitle"
            )}
          </p>
        </div>

        <button
          type="button"
          className="udm-close"
          onClick={
            closeModal
          }
          aria-label={t(
            "actions.close"
          )}
          title={t(
            "actions.close"
          )}
        >
          <FiX />
        </button>
      </header>
    );

  if (
    loading
  ) {
    return (
      <div
        className="udm-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-details-modal-title"
        onMouseDown={(
          event
        ) => {
          if (
            event.target ===
            event.currentTarget
          ) {
            closeModal();
          }
        }}
      >
        <div
          className="udm-modal"
          onMouseDown={(
            event
          ) =>
            event.stopPropagation()
          }
        >
          {renderHeader()}

          <div className="udm-status-panel">
            <div className="udm-status-spinner" />

            <span>
              {t(
                "status.loading"
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (
    !form
  ) {
    return (
      <div
        className="udm-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-details-modal-title"
        onMouseDown={(
          event
        ) => {
          if (
            event.target ===
            event.currentTarget
          ) {
            closeModal();
          }
        }}
      >
        <div
          className="udm-modal"
          onMouseDown={(
            event
          ) =>
            event.stopPropagation()
          }
        >
          {renderHeader()}

          <div className="udm-status-panel">
            <span>
              {error ||
                t(
                  "status.empty"
                )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="udm-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-details-modal-title"
      onMouseDown={(
        event
      ) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          closeModal();
        }
      }}
    >
      <div
        className="udm-modal"
        onMouseDown={(
          event
        ) =>
          event.stopPropagation()
        }
      >
        {renderHeader()}

        <div className="udm-form">
          <div className="udm-body">
            {/* ==================================================
                LEFT — PROFILE PHOTO
               ================================================== */}

            <aside className="udm-profile">
              <div className="udm-profile-sticky">
                <button
                  type="button"
                  className="udm-avatar"
                  onClick={
                    handleFilePick
                  }
                  aria-label={t(
                    "photo.aria"
                  )}
                  title={t(
                    "photo.aria"
                  )}
                >
                  <span className="udm-avatar-inner">
                    {visibleProfilePicture ? (
                      <img
                        src={
                          visibleProfilePicture
                        }
                        alt={
                          file
                            ? t(
                                "photo.alt_new"
                              )
                            : t(
                                "photo.alt"
                              )
                        }
                      />
                    ) : (
                      <span className="udm-avatar-placeholder">
                        <FiUser />

                        <span className="udm-avatar-plus">
                          <FiEdit3 />
                        </span>
                      </span>
                    )}
                  </span>
                </button>

                <input
                  ref={
                    fileInputRef
                  }
                  type="file"
                  accept="image/*,.heic,.heif"
                  className="udm-hidden-input"
                  onChange={
                    handleProfileFile
                  }
                />

                <div className="udm-photo-copy">
                  <strong>
                    {visibleProfilePicture
                      ? t(
                          "photo.change"
                        )
                      : t(
                          "photo.add"
                        )}
                  </strong>

                  <span>
                    {t(
                      "photo.formats"
                    )}
                  </span>

                  <span>
                    {t(
                      "photo.max_size"
                    )}
                  </span>
                </div>

                <div className="udm-photo-actions">
                  {file &&
                    profileSourcePreview && (
                      <button
                        type="button"
                        className="udm-photo-link"
                        onClick={() =>
                          setProfileEditorOpen(
                            true
                          )
                        }
                      >
                        {t(
                          "photo.adjust"
                        )}
                      </button>
                    )}

                  <button
                    type="button"
                    className="udm-photo-link"
                    onClick={
                      handleFilePick
                    }
                  >
                    {t(
                      "photo.change"
                    )}
                  </button>
                </div>
              </div>
            </aside>

            {/* ==================================================
                RIGHT — FORM
               ================================================== */}

            <main className="udm-main">
              <div className="udm-row two">
                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiUser
                    }
                  >
                    {t(
                      "fields.name.label"
                    )}
                  </FieldLabel>

                  <input
                    className="udm-input"
                    type="text"
                    value={
                      form.name
                    }
                    onChange={(
                      event
                    ) =>
                      onChange(
                        "name",
                        event
                          .target
                          .value
                      )
                    }
                    placeholder={t(
                      "fields.name.placeholder"
                    )}
                    autoComplete="name"
                  />
                </div>

                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiShield
                    }
                  >
                    {t(
                      "fields.role.label"
                    )}
                  </FieldLabel>

                  <Select
                    isClearable
                    classNamePrefix="udm-select"
                    styles={
                      userDetailsSelectStyles
                    }
                    options={
                      roleOptions
                    }
                    value={
                      roleValue
                    }
                    onChange={(
                      option
                    ) =>
                      onChange(
                        "role",
                        option
                          ?.value ||
                          ""
                      )
                    }
                    placeholder={t(
                      "fields.role.placeholder"
                    )}
                  />
                </div>
              </div>

              <div className="udm-row">
                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiMail
                    }
                  >
                    {t(
                      "fields.email.label"
                    )}
                  </FieldLabel>

                  <input
                    className="udm-input"
                    type="email"
                    value={
                      form.email
                    }
                    onChange={(
                      event
                    ) =>
                      onChange(
                        "email",
                        event
                          .target
                          .value
                      )
                    }
                    placeholder={t(
                      "fields.email.placeholder"
                    )}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="udm-row two">
                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiPhone
                    }
                  >
                    {t(
                      "fields.phone.label"
                    )}
                  </FieldLabel>

                  <input
                    className="udm-input"
                    type="tel"
                    value={
                      form.phoneNumber
                    }
                    onChange={(
                      event
                    ) =>
                      onChange(
                        "phoneNumber",
                        event
                          .target
                          .value
                      )
                    }
                    placeholder={t(
                      "fields.phone.placeholder"
                    )}
                    autoComplete="tel"
                  />
                </div>

                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiCalendar
                    }
                  >
                    {t(
                      "fields.dob.label"
                    )}
                  </FieldLabel>

                  <div className="udm-input-wrap">
                    <input
                      ref={
                        dobRef
                      }
                      className="udm-input has-trailing-action"
                      type="date"
                      value={
                        form.dateOfBirth
                      }
                      onChange={(
                        event
                      ) =>
                        onChange(
                          "dateOfBirth",
                          event
                            .target
                            .value
                        )
                      }
                    />

                    <button
                      type="button"
                      className="udm-input-action"
                      onClick={() =>
                        openPicker(
                          dobRef
                        )
                      }
                      aria-label={t(
                        "aria.open_calendar"
                      )}
                      title={t(
                        "aria.open_calendar"
                      )}
                    >
                      <FiCalendar />
                    </button>
                  </div>
                </div>
              </div>

              <div className="udm-row two">
                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiCreditCard
                    }
                  >
                    {t(
                      "fields.idNumber.label"
                    )}
                  </FieldLabel>

                  <input
                    className="udm-input"
                    type="text"
                    value={
                      form.idNumber
                    }
                    onChange={(
                      event
                    ) =>
                      onChange(
                        "idNumber",
                        event
                          .target
                          .value
                      )
                    }
                    placeholder={t(
                      "fields.idNumber.placeholder"
                    )}
                  />
                </div>

                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiCalendar
                    }
                  >
                    {t(
                      "fields.idExpiryDate.label"
                    )}
                  </FieldLabel>

                  <div className="udm-input-wrap">
                    <input
                      ref={
                        idExpRef
                      }
                      className="udm-input has-trailing-action"
                      type="date"
                      value={
                        form.idExpiryDate
                      }
                      onChange={(
                        event
                      ) =>
                        onChange(
                          "idExpiryDate",
                          event
                            .target
                            .value
                        )
                      }
                    />

                    <button
                      type="button"
                      className="udm-input-action"
                      onClick={() =>
                        openPicker(
                          idExpRef
                        )
                      }
                      aria-label={t(
                        "aria.open_calendar"
                      )}
                      title={t(
                        "aria.open_calendar"
                      )}
                    >
                      <FiCalendar />
                    </button>
                  </div>
                </div>
              </div>

              <div className="udm-row">
                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiHome
                    }
                  >
                    {t(
                      "fields.address.label"
                    )}
                  </FieldLabel>

                  <input
                    className="udm-input"
                    type="text"
                    value={
                      form.address
                    }
                    onChange={(
                      event
                    ) =>
                      onChange(
                        "address",
                        event
                          .target
                          .value
                      )
                    }
                    placeholder={t(
                      "fields.address.placeholder"
                    )}
                    autoComplete="street-address"
                  />
                </div>
              </div>

              <div className="udm-row three">
                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiMapPin
                    }
                  >
                    {t(
                      "fields.postalCode.label"
                    )}
                  </FieldLabel>

                  <input
                    className="udm-input"
                    type="text"
                    value={
                      form.postalCode
                    }
                    onChange={(
                      event
                    ) =>
                      onChange(
                        "postalCode",
                        event
                          .target
                          .value
                      )
                    }
                    placeholder={t(
                      "fields.postalCode.placeholder"
                    )}
                    autoComplete="postal-code"
                  />
                </div>

                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiMapPin
                    }
                  >
                    {t(
                      "fields.city.label"
                    )}
                  </FieldLabel>

                  <input
                    className="udm-input"
                    type="text"
                    value={
                      form.city
                    }
                    onChange={(
                      event
                    ) =>
                      onChange(
                        "city",
                        event
                          .target
                          .value
                      )
                    }
                    placeholder={t(
                      "fields.city.placeholder"
                    )}
                    autoComplete="address-level2"
                  />
                </div>

                <div className="udm-field">
                  <FieldLabel
                    required
                    icon={
                      FiGlobe
                    }
                  >
                    {t(
                      "fields.country.label"
                    )}
                  </FieldLabel>

                  <input
                    className="udm-input"
                    type="text"
                    value={
                      form.country
                    }
                    onChange={(
                      event
                    ) =>
                      onChange(
                        "country",
                        event
                          .target
                          .value
                      )
                    }
                    placeholder={t(
                      "fields.country.placeholder"
                    )}
                    autoComplete="country-name"
                  />
                </div>
              </div>

              {error && (
                <div className="udm-error">
                  {
                    error
                  }
                </div>
              )}
            </main>
          </div>

          <footer className="udm-footer">
            <button
              type="button"
              className="udm-btn udm-btn-ghost"
              onClick={
                closeModal
              }
              disabled={
                saving
              }
            >
              {t(
                "actions.cancel"
              )}
            </button>

            <button
              type="button"
              className="udm-btn udm-btn-primary"
              onClick={
                handleSave
              }
              disabled={
                saving ||
                !dirty
              }
            >
              <FiSave
                aria-hidden="true"
              />

              <span>
                {saving
                  ? t(
                      "actions.saving"
                    )
                  : t(
                      "actions.save_changes"
                    )}
              </span>
            </button>
          </footer>
        </div>

        {profileEditorOpen &&
          profileSourcePreview && (
            <div
              className="udm-photo-editor-backdrop"
              onMouseDown={(
                event
              ) => {
                if (
                  event.target ===
                  event.currentTarget
                ) {
                  setProfileEditorOpen(
                    false
                  );
                }
              }}
            >
              <div
                className="udm-photo-editor"
                onMouseDown={(
                  event
                ) =>
                  event.stopPropagation()
                }
              >
                <div className="udm-photo-editor-header">
                  <div>
                    <h3>
                      {t(
                        "photo.editor.title"
                      )}
                    </h3>

                    <p>
                      {t(
                        "photo.editor.subtitle"
                      )}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="udm-photo-editor-close"
                    onClick={() =>
                      setProfileEditorOpen(
                        false
                      )
                    }
                    aria-label={t(
                      "actions.close"
                    )}
                    title={t(
                      "actions.close"
                    )}
                  >
                    <FiX />
                  </button>
                </div>

                <div className="udm-photo-editor-preview">
                  <div className="udm-photo-editor-circle">
                    <img
                      src={
                        profileSourcePreview
                      }
                      alt={t(
                        "photo.alt_new"
                      )}
                      style={{
                        objectPosition: `${profilePositionX}% ${profilePositionY}%`,
                        transform: `scale(${profileZoom})`,
                      }}
                    />
                  </div>
                </div>

                <div className="udm-photo-editor-controls">
                  <label>
                    <span>
                      {t(
                        "photo.editor.zoom"
                      )}
                    </span>

                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.01"
                      value={
                        profileZoom
                      }
                      onChange={(
                        event
                      ) =>
                        setProfileZoom(
                          Number(
                            event
                              .target
                              .value
                          )
                        )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      {t(
                        "photo.editor.horizontal"
                      )}
                    </span>

                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={
                        profilePositionX
                      }
                      onChange={(
                        event
                      ) =>
                        setProfilePositionX(
                          Number(
                            event
                              .target
                              .value
                          )
                        )
                      }
                    />
                  </label>

                  <label>
                    <span>
                      {t(
                        "photo.editor.vertical"
                      )}
                    </span>

                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={
                        profilePositionY
                      }
                      onChange={(
                        event
                      ) =>
                        setProfilePositionY(
                          Number(
                            event
                              .target
                              .value
                          )
                        )
                      }
                    />
                  </label>
                </div>

                <div className="udm-photo-editor-actions">
                  <button
                    type="button"
                    className="udm-btn udm-btn-ghost"
                    onClick={() =>
                      setProfileEditorOpen(
                        false
                      )
                    }
                  >
                    {t(
                      "actions.cancel"
                    )}
                  </button>

                  <button
                    type="button"
                    className="udm-btn udm-btn-primary"
                    onClick={
                      confirmProfileAdjustment
                    }
                  >
                    {t(
                      "photo.editor.use_photo"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}